import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";

export default function Contact() {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    if (!formData.get("name") || !formData.get("email") || !formData.get("message")) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSending(true);
    try {
      const response = await fetch("/", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData).toString(),
      });
      if (response.ok) {
        setSent(true);
        toast.success("Message sent! I'll get back to you soon.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#0A0A0A] relative"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        {/* Left text */}
        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <p className="font-mono text-sm uppercase tracking-widest font-bold text-[#FE4A90] mb-3">
            Get in Touch
          </p>
          <h2
            data-testid="contact-title"
            className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#FFFDF5] uppercase"
          >
            Let's
            <br />
            <span className="text-stroke-thin" style={{ WebkitTextStroke: "2px #FFFDF5" }}>
              Build
            </span>
          </h2>
          <p className="font-body text-base sm:text-lg text-[#a0a0a0] mt-6 max-w-sm leading-relaxed">
            Have a project in mind? Whether you need a new website, a refresh,
            or just want to chat about possibilities &mdash; I'd love to hear
            from you.
          </p>

          <div className="mt-10 space-y-3">
            <p className="font-mono text-sm uppercase tracking-widest font-bold text-[#FFD166]">
              connor@corcorandesign.com
            </p>
            <p className="font-mono text-sm uppercase tracking-widest font-bold text-[#FFD166]">
              (510) 501-9904
            </p>
            <p className="font-mono text-sm uppercase tracking-widest text-[#525252]">
              Available for freelance
            </p>
          </div>
        </motion.div>

        {/* Right form */}
        <motion.div
          className="lg:col-span-7"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.15 }}
        >
          {sent ? (
            <motion.div
              data-testid="contact-success"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="brutal-card bg-[#06D6A0] p-10 md:p-14 text-center"
            >
              <CheckCircle size={48} className="text-[#0A0A0A] mx-auto" />
              <h3 className="font-heading text-2xl sm:text-3xl text-[#0A0A0A] mt-4">
                Message Received!
              </h3>
              <p className="font-body text-base text-[#0A0A0A] mt-2 opacity-80">
                I'll get back to you within 24 hours.
              </p>
              <button
                data-testid="contact-send-another"
                onClick={() => setSent(false)}
                className="brutal-btn bg-[#0A0A0A] text-[#FFFDF5] px-6 py-3 text-sm mt-8"
              >
                Send Another Message
              </button>
            </motion.div>
          ) : (
            <form
              data-testid="contact-form"
              name="contact"
              method="POST"
              data-netlify="true"
              netlify-honeypot="bot-field"
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <input type="hidden" name="form-name" value="contact" />
              <p className="hidden">
                <label>Don't fill this out: <input name="bot-field" /></label>
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="font-mono text-xs uppercase tracking-widest font-bold text-[#FFFDF5] mb-2 block">
                    Name *
                  </label>
                  <input
                    data-testid="contact-input-name"
                    id="name"
                    type="text"
                    name="name"
                    placeholder="Your name"
                    className="brutal-input w-full px-4 py-3 text-[#0A0A0A] text-base"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="font-mono text-xs uppercase tracking-widest font-bold text-[#FFFDF5] mb-2 block">
                    Email *
                  </label>
                  <input
                    data-testid="contact-input-email"
                    id="email"
                    type="email"
                    name="email"
                    placeholder="you@business.com"
                    className="brutal-input w-full px-4 py-3 text-[#0A0A0A] text-base"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="business_name" className="font-mono text-xs uppercase tracking-widest font-bold text-[#FFFDF5] mb-2 block">
                  Business Name
                </label>
                <input
                  data-testid="contact-input-business"
                  id="business_name"
                  type="text"
                  name="business_name"
                  placeholder="Your business (optional)"
                  className="brutal-input w-full px-4 py-3 text-[#0A0A0A] text-base"
                />
              </div>

              <div>
                <label htmlFor="message" className="font-mono text-xs uppercase tracking-widest font-bold text-[#FFFDF5] mb-2 block">
                  Message *
                </label>
                <textarea
                  data-testid="contact-input-message"
                  id="message"
                  name="message"
                  rows={5}
                  placeholder="Tell me about your project..."
                  className="brutal-input w-full px-4 py-3 text-[#0A0A0A] text-base resize-none"
                />
              </div>

              <button
                data-testid="contact-submit-btn"
                type="submit"
                disabled={sending}
                className="brutal-btn bg-[#FE4A90] text-white px-8 py-4 text-sm flex items-center gap-3 disabled:opacity-60"
              >
                {sending ? "Sending..." : "Send Message"}
                <Send size={18} />
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
}
